import PropTypes from 'prop-types';
import { useEffect, useImperativeHandle } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Row, Col, Form } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import isEqual from 'lodash.isequal';

import { usePrevious } from '_hooks';
import { theme } from '_constants/theme';
import { FIELDS_KEYS } from '_constants/forms';

import { getFieldProps } from '_helpers/forms/fields';

import Card from '_components/atomic/layouts/Card';
import BadgeSelect2Field from '_components/atomic/fields/BadgeSelect2Field';

const StateAndEntitySelectForm2 = ({
  submitRef,
  error,
  geolocation,
  defaultValues,
  onSubmit,
}) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    setValue,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);
  const values = useWatch({ control });
  const prevValues = usePrevious(values);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (
      !values[FIELDS_KEYS.organizedState] &&
      geolocation?.region &&
      geolocation?.countryCode === 'US'
    ) {
      setValue(FIELDS_KEYS.organizedState, geolocation.region);
    }
  }, [values, geolocation, setValue]);

  useEffect(() => {
    if (!isEqual(prevValues, values)) {
      onSubmit && onSubmit(values);
    }
  }, [prevValues, values, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [reset, prevDefaultValues, defaultValues]);

  useImperativeHandle(submitRef, () => ({
    submit: handleSubmit(onSubmit),
  }));

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} md={1} />
        <Col xs={12} md={5}>
          <BadgeSelect2Field
            {...getFieldProps(FIELDS_KEYS.entity)}
            badgeLabel="1"
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12} md={5}>
          <BadgeSelect2Field
            {...getFieldProps(FIELDS_KEYS.organizedState)}
            badgeLabel="2"
            bottomSpace={
              isMobile &&
              values[FIELDS_KEYS.entity] &&
              values[FIELDS_KEYS.organizedState]
                ? undefined
                : theme.spaces.regular
            }
            control={control}
          />
        </Col>
        <Col xs={12} md={1} />
      </Row>

      {error && (
        <Card.Text weight={700} color={theme.colors.orange[600]}>
          {error?.message}
        </Card.Text>
      )}
    </Form>
  );
};

StateAndEntitySelectForm2.propTypes = {
  submitRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  error: PropTypes.object,
  geolocation: PropTypes.object,
  prices: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default StateAndEntitySelectForm2;
